import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { useAuth } from '../contexts/AuthContext';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import tenantService, { Tenant, UpdateTenantData } from '../services/tenant.service';
import { PageHeader, EmptyState, LoadingState, StatusBadge } from '../components/ui';
import { Icon } from '../components/icons';
import { datum, fehlerText } from '../vertrieb/format';

const ausMandant = (t: Tenant): UpdateTenantData => ({ name: t.name, email: t.email || '', maxUsers: t.maxUsers, isActive: t.isActive });

/** Stammdaten des eigenen Mandanten. Bearbeiten darf, wer `admin.mandant` hat (Inhaber:in). */
const TenantManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { darf } = useBerechtigung();
  const { push } = useToast();
  const [mandant, setMandant] = useState<Tenant | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeitet, setBearbeitet] = useState(false);
  const [formular, setFormular] = useState<UpdateTenantData>({});
  const [speichert, setSpeichert] = useState(false);

  const istEigentuemer = darf('admin.mandant');
  // Plaetze und Aktiv-Schalter sind Plattformsache (Server: admin.mandant.alle).
  const istPlattformAdmin = darf('admin.mandant.alle');
  const mandantId = user?.tenantId;

  const laden = useCallback(async () => {
    if (!mandantId) { setLaedt(false); return; }
    try {
      const t = await tenantService.getTenant(mandantId);
      setMandant(t ?? null);
      if (t) setFormular(ausMandant(t));
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Mandant konnte nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [mandantId]);

  useEffect(() => { laden(); }, [laden]);

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mandantId) return;
    setSpeichert(true);
    try {
      const neu = await tenantService.updateTenant(mandantId, formular);
      setMandant(neu);
      setBearbeitet(false);
      push({ message: 'Mandant gespeichert.', severity: 'success' });
    } catch (err) {
      setFehler(fehlerText(err, 'Speichern fehlgeschlagen'));
    } finally {
      setSpeichert(false);
    }
  };

  const abbrechen = () => {
    if (mandant) setFormular(ausMandant(mandant));
    setBearbeitet(false);
    setFehler(null);
  };

  const kopf = (
    <PageHeader
      eyebrow="Verwaltung"
      title="Mandant"
      subtitle="Firmenname, Kontaktadresse und die Zahl der Benutzerplätze."
      actions={istEigentuemer && mandant && !bearbeitet ? (
        <button type="button" className="btn btn-primary" onClick={() => setBearbeitet(true)}><Icon name="edit" size={14} /> Bearbeiten</button>
      ) : undefined}
    />
  );

  if (!mandantId) {
    return (
      <div className="page-container verwaltung-seite">
        {kopf}
        <EmptyState icon="settings" title="Sie sind keinem Mandanten zugeordnet" description="Bitte wenden Sie sich an die Inhaber:in Ihres Mandanten." />
      </div>
    );
  }

  if (laedt) return <div className="page-container verwaltung-seite">{kopf}<LoadingState rows={3} label="Mandant wird geladen" /></div>;

  if (!mandant) {
    return (
      <div className="page-container verwaltung-seite">
        {kopf}
        <EmptyState icon="settings" title="Mandant nicht gefunden" description={fehler ?? undefined} action={{ label: 'Erneut laden', onClick: laden }} />
      </div>
    );
  }

  const belegt = mandant.users?.length ?? 0;
  const anteil = mandant.maxUsers ? Math.min(100, Math.round((belegt / mandant.maxUsers) * 100)) : 0;

  return (
    <div className="page-container verwaltung-seite">
      {kopf}
      {fehler && <div className="alert alert-error">{fehler}</div>}

      {bearbeitet ? (
        <form className="card inhalt-formular" onSubmit={speichern} aria-label="Mandant bearbeiten">
          <div className="inhalt-felder">
            <label>Firmenname *<input required value={formular.name || ''} onChange={(e) => setFormular({ ...formular, name: e.target.value })} /></label>
            <label>Kontakt-E-Mail<input type="email" value={formular.email || ''} onChange={(e) => setFormular({ ...formular, email: e.target.value })} /></label>
            <label>
              Benutzerplätze
              <input type="number" min={Math.max(1, belegt)} value={formular.maxUsers ?? 10} disabled={!istPlattformAdmin} title={istPlattformAdmin ? undefined : 'Nur Plattform-Administratoren ändern die Benutzerplätze'} onChange={(e) => setFormular({ ...formular, maxUsers: parseInt(e.target.value, 10) || 1 })} />
            </label>
          </div>
          <label className="akte-checkbox">
            <input type="checkbox" checked={Boolean(formular.isActive)} disabled={!istPlattformAdmin} onChange={(e) => setFormular({ ...formular, isActive: e.target.checked })} /> Mandant aktiv
          </label>
          {formular.isActive === false && <div className="alert alert-warning">Ein inaktiver Mandant sperrt alle Benutzer bis auf weiteres aus.</div>}
          <div className="btn-group">
            <button type="submit" className="btn btn-primary" disabled={speichert}>{speichert ? 'Speichert…' : 'Speichern'}</button>
            <button type="button" className="btn btn-ghost" onClick={abbrechen}>Abbrechen</button>
          </div>
        </form>
      ) : (
        <section className="card" aria-label="Mandant">
          <div className="inhalt-ergebnis__kopf">
            <h2 className="card-title">{mandant.name}</h2>
            {mandant.isActive ? <StatusBadge tone="success">Aktiv</StatusBadge> : <StatusBadge tone="danger">Inaktiv</StatusBadge>}
          </div>
          <dl className="sales-facts">
            <div><dt>Kontakt</dt><dd>{mandant.email ? <a href={`mailto:${mandant.email}`}>{mandant.email}</a> : 'keine Adresse hinterlegt'}</dd></div>
            <div><dt>Angelegt</dt><dd>{datum(mandant.createdAt)}</dd></div>
          </dl>
        </section>
      )}

      <section className="card" aria-label="Benutzerplätze">
        <div className="inhalt-ergebnis__kopf">
          <h2 className="card-title">Benutzerplätze</h2>
          <Link to="/user-management" className="btn btn-secondary btn-sm"><Icon name="users" size={14} /> Benutzer verwalten</Link>
        </div>
        <p><strong>{belegt}</strong> von {mandant.maxUsers} belegt</p>
        <div className={`auslastung ${anteil >= 100 ? 'auslastung--voll' : anteil >= 80 ? 'auslastung--knapp' : ''}`} role="progressbar" aria-valuemin={0} aria-valuemax={mandant.maxUsers} aria-valuenow={belegt} aria-label="Belegte Plätze">
          <span style={{ width: `${anteil}%` }} />
        </div>
        {anteil >= 100 && <p className="text-xs text-secondary">Alle Plätze sind belegt. {istEigentuemer ? 'Unter „Bearbeiten“ lässt sich die Zahl erhöhen.' : 'Die Inhaber:in kann die Zahl erhöhen.'}</p>}
      </section>
    </div>
  );
};

export default TenantManagementPage;
